import {
  ADD,
  DELETE,
  FORM_NAME,
  LOAD,
  LOAD_BY_ORG_ID,
  UPDATE,
} from 'model/group';
import { createAdminPriviledge } from 'util/rbac';
import {
  addGroup,
  deleteGroup,
  load,
  loadByOrgId,
  updateGroup,
} from 'sdk/group';
import { HTTP_STATUS_CODE_403, ROWS_PER_PAGE_MAX } from 'consts';
import { omit, prop, uniqBy } from 'ramda';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { startRequestTimer } from 'reducers/metrics';
import {
  SUCCESS,
  FAILURE,
  UNAUTH,
  createActionSuccess,
  UNAUTHORIZED,
} from '../util/actions';
import { getAuth } from '../util/auth';
import { callAction, getError } from '../util/saga';
import { addGroupAdminCall } from './group_admin';
import { updateOrgCall } from './org';

/** Matches sp_get_mgd_groups_by_org as V1 Get Groups API: qp_limit max 100 (same as ROWS_PER_PAGE_MAX). */
const ADMIN_ORG_GROUP_PAGE_SIZE = ROWS_PER_PAGE_MAX;
const ADMIN_ORG_GROUPS_MAX_QP_OFFSET = 1000;

const getDetails = (e) => {
  let { error: rawError, message, statusCode, response = {} } = e;

  if (e.response) {
    statusCode = e.response.status;
    message = e.response.statusText;
  }

  if (!e.response && e.data) {
    response = e.data;
    rawError = e.data.error;
  }

  return { message, rawError, response, statusCode };
};

const normalizeAdminGroupPage = (raw) => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && raw.adminGroup) {
    return raw.adminGroup;
  }
  return [];
};

export function* loadGroupsByOrgIdAllPages({ data = {}, form, meta }) {
  const timer = startRequestTimer(LOAD_BY_ORG_ID.ACTION);
  const callData = data;
  const { organizationId } = callData;

  if (organizationId == null || !Number.isFinite(organizationId)) {
    yield put(createActionSuccess(LOAD_BY_ORG_ID)([], callData, meta));
    yield put(timer.finish(SUCCESS));
    return;
  }

  const auth = yield select(getAuth);
  const merged = [];
  let offset = 0;

  try {
    while (true) {
      const raw = yield call(
        loadByOrgId,
        {
          organizationId,
          limit: ADMIN_ORG_GROUP_PAGE_SIZE,
          offset,
        },
        auth
      );
      const page = normalizeAdminGroupPage(raw);
      if (page.length === 0) {
        break;
      }
      merged.push(...page);
      if (page.length < ADMIN_ORG_GROUP_PAGE_SIZE) {
        break;
      }
      offset += ADMIN_ORG_GROUP_PAGE_SIZE;
      if (offset > ADMIN_ORG_GROUPS_MAX_QP_OFFSET) {
        break;
      }
    }

    const result = uniqBy(prop('id'), merged);
    yield put(createActionSuccess(LOAD_BY_ORG_ID)(result, callData, meta));
    yield put(timer.finish(SUCCESS));
  } catch (e) {
    const { message, statusCode, rawError, response } = getDetails(e);

    if (statusCode === HTTP_STATUS_CODE_403) {
      yield put({
        type: UNAUTHORIZED.ACTION,
        data: {
          authToken: {
            error: rawError,
            unauthorized: true,
          },
        },
      });
      yield put(timer.finish(UNAUTH));
      return;
    }

    const error = getError(response, message, rawError);
    if (error) {
      yield put({ data, error, type: LOAD_BY_ORG_ID.FAILURE });
      yield put(timer.finish(FAILURE));
      return;
    }

    yield put(createActionSuccess(LOAD_BY_ORG_ID)([], callData, meta));
    yield put(timer.finish(SUCCESS));
  }
}

function* postAddGroup({ data, result }) {
  const { orgId, manageOther, orgAdminGroupId } = data;
  const postResult = { ...result };
  const { id } = postResult;

  if (orgAdminGroupId === 'self') {
    const orgUpdateData = {
      id: orgId,
      adminGroupId: id,
    };
    yield updateOrgCall()({ data: orgUpdateData });
  }
  if (orgAdminGroupId === 'self' || manageOther === 'true') {
    const groupAdminData = createAdminPriviledge({
      groupId: id,
      adminGroupId: id,
    });

    const addGroupAdminResult = yield addGroupAdminCall()({
      data: groupAdminData,
    });
    const restOfData = omit(
      ['id', 'offset', 'href', 'createdAt', 'updatedAt'],
      addGroupAdminResult
    );
    postResult.groupAdmins.push(restOfData);
  }
  return postResult;
}

const prepareData = (data) =>
  omit(['orgId', 'orgAdminGroupId', 'manageOther', 'managedBy'], data);

export default function* saga() {
  yield takeEvery(
    LOAD.ACTION,
    callAction({
      api: load,
      dataName: 'adminGroup',
      type: LOAD,
    })
  );

  yield takeEvery(LOAD_BY_ORG_ID.ACTION, loadGroupsByOrgIdAllPages);

  yield takeEvery(
    ADD.ACTION,
    callAction({
      api: addGroup,
      formName: FORM_NAME,
      formatSuccessMessage: (data) =>
        `${data.name} Group has been successfully added.`,
      postAction: postAddGroup,
      prepareData,
      type: ADD,
    })
  );

  yield takeEvery(
    DELETE.ACTION,
    callAction({
      api: deleteGroup,
      formatSuccessMessage: (data) =>
        `${data.name} Group has been successfully removed.`,
      type: DELETE,
    })
  );

  yield takeEvery(
    UPDATE.ACTION,
    callAction({
      api: updateGroup,
      formName: FORM_NAME,
      formatSuccessMessage: (data) =>
        `${data.name} Group has been successfully updated.`,
      prepareData,
      type: UPDATE,
    })
  );
}
